import React, { useState, useEffect, useRef } from 'react';
import { Button, Popover, Checkbox, Space, Divider } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

/**
 * 表格控制欄組件
 * 包含列可見性控制和水平滾動按鈕
 */
const TableControlBar = ({ columns, visibleColumns, onChange, tableContainerRef }) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // 過濾掉不需要控制顯示的列（如操作列、收藏列等）
  const controllableColumns = columns.filter(col => 
    col.key && 
    col.key !== 'action' && 
    col.key !== 'watchlist_action' &&
    col.dataIndex !== 'company_symbol' // 公司代碼列永遠顯示
  );

  // 處理單個列的顯示/隱藏切換
  const handleColumnToggle = (columnKey) => {
    const newVisibleColumns = visibleColumns.includes(columnKey)
      ? visibleColumns.filter(key => key !== columnKey)
      : [...visibleColumns, columnKey];
    onChange(newVisibleColumns);
  };

  // 全選
  const handleSelectAll = () => {
    const allKeys = controllableColumns.map(col => col.key);
    onChange(allKeys);
  };

  // 全不選
  const handleDeselectAll = () => {
    onChange([]);
  };

  // 獲取列的顯示名稱
  const getColumnTitle = (column) => {
    if (typeof column.title === 'function') {
      // 如果title是函數，嘗試提取文本內容
      const titleElement = column.title();
      if (React.isValidElement(titleElement)) {
        return titleElement.props.children || column.dataIndex || column.key;
      }
      return titleElement;
    }
    return column.title || column.dataIndex || column.key;
  };

  // 獲取滾動容器
  const getScrollContainer = () => {
    if (tableContainerRef && tableContainerRef.current) {
      return tableContainerRef.current.querySelector('.ant-table-body')
        || tableContainerRef.current.querySelector('.ant-table-content')
        || tableContainerRef.current.querySelector('.ant-table-container');
    }
    return document.querySelector('.ant-table-body') 
      || document.querySelector('.ant-table-content')
      || document.querySelector('.ant-table-container');
  };

  // 檢查滾動狀態
  const checkScrollability = () => {
    const scrollContainer = getScrollContainer();
    
    if (scrollContainer) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
      const maxScroll = scrollWidth - clientWidth;
      
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < maxScroll - 1);
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }
  };

  // 滾動表格
  const scrollTable = (direction) => {
    const scrollContainer = getScrollContainer();
    
    if (!scrollContainer) return;
    
    const scrollAmount = 200;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const newScrollPosition = direction === 'left'
      ? Math.max(0, scrollLeft - scrollAmount)
      : Math.min(scrollWidth - clientWidth, scrollLeft + scrollAmount);
    
    scrollContainer.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
    
    // 滾動後更新按鈕狀態
    setTimeout(checkScrollability, 100);
  };

  // 監聽表格變化並檢查滾動狀態
  useEffect(() => {
    // 延遲檢查，確保表格已經渲染
    const timer = setTimeout(() => {
      checkScrollability();
    }, 300);

    // 添加滾動事件監聽
    const scrollContainer = getScrollContainer();
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollability);
    }

    return () => {
      clearTimeout(timer);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScrollability);
      }
    };
  }, [columns, visibleColumns]);

  const content = (
    <div style={{ width: 300, maxHeight: 400, overflowY: 'auto' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ marginBottom: 8 }}>
          <Space>
            <Button size="small" onClick={handleSelectAll}>全選</Button>
            <Button size="small" onClick={handleDeselectAll}>全不選</Button>
          </Space>
        </div>
        <Divider style={{ margin: '8px 0' }} />
        {controllableColumns.map(column => (
          <Checkbox
            key={column.key}
            checked={visibleColumns.includes(column.key)}
            onChange={() => handleColumnToggle(column.key)}
          >
            {getColumnTitle(column)}
          </Checkbox>
        ))}
      </Space>
    </div>
  );

  return (
    <div style={{ 
      marginBottom: '16px', 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      {/* 左側：欄位設置 */}
      <Popover
        content={content}
        title="選擇顯示欄位"
        trigger="click"
        open={popoverVisible}
        onOpenChange={setPopoverVisible}
        placement="bottomLeft"
      >
        <Button 
          icon={<SettingOutlined />}
          type="default"
        >
          欄位設置
        </Button>
      </Popover>

      {/* 中間：滾動按鈕 */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button 
          onClick={() => scrollTable('left')}
          disabled={!canScrollLeft}
        >
          ← 左滾動
        </Button>
        <Button 
          onClick={() => scrollTable('right')}
          disabled={!canScrollRight}
        >
          右滾動 →
        </Button>
      </div>

      {/* 右側：空白（為了保持布局平衡） */}
      <div style={{ width: '100px' }}></div>
    </div>
  );
};

/**
 * Hook for managing column visibility
 */
export const useColumnVisibility = (allColumns, defaultVisibleKeys = null) => {
  // 默認全選所有可控制的列
  const getInitialKeys = () => {
    if (defaultVisibleKeys) return defaultVisibleKeys;
    
    return allColumns
      .filter(col => col.key && col.key !== 'action' && col.key !== 'watchlist_action')
      .map(col => col.key);
  };
  
  const [visibleColumns, setVisibleColumns] = useState(getInitialKeys());

  // 當allColumns改變時，重新計算初始keys
  useEffect(() => {
    setVisibleColumns(getInitialKeys());
  }, [allColumns.length]); // 只在列數量改變時更新

  // 根據可見性過濾列
  const filteredColumns = allColumns.filter(col => {
    // 永遠顯示的列
    if (!col.key || col.key === 'action' || col.key === 'watchlist_action' || col.dataIndex === 'company_symbol') {
      return true;
    }
    // 根據可見性設置顯示
    return visibleColumns.includes(col.key);
  });

  return {
    visibleColumns,
    setVisibleColumns,
    filteredColumns
  };
};

export default TableControlBar;
